import { useTranslation } from 'react-i18next'
import { extractedErrorObject } from '../api/swagger'
import { SnackbarSeverity } from '../components/Alerts/SnackbarAlert'
import { useSnackbarContext } from '../components/Context/SnackbarContext'

type ErrorObject = { code: string; message: string }
/**
 * Represents the possible severity levels for the snackbar messages.
 * This type is used to avoid the need to import the SnackbarSeverity enum.
 */
type SnackbarSeverityType = `${SnackbarSeverity}`

/**
 * useAppSnackbar
 *
 * Custom hook to simplify showing messages and handling errors via the application's Snackbar system.
 *
 * This hook centralizes the display of feedback messages by exposing two main handlers:
 *
 * - `handleError`: Extracts and formats error information to show a user-friendly error message.
 * - `handleMessage`: Displays a custom message with optional severity (default is 'success').
 *
 * Also provides manual access to the snackbar's state and configuration functions.
 *
 * @returns {object} - An object containing helper functions:
 *
 * @property {Function} handleError
 *    Processes an unknown error, extracts meaningful message and displays it.
 *    @param {unknown} error - The error object to handle.
 *    @param {string} [defaultErrorMessage] - Optional fallback message if error info is missing.
 *
 * @property {Function} handleMessage
 *    Displays a custom message with a given severity (defaults to "success").
 *    @param {string} message - The message to display.
 *    @param {SnackbarSeverityType} [severity="success"] - Optional severity level ('success' | 'error' | 'warning' | 'info').
 *
 * @property {Function} setSnackbarState - Manually shows/hides the snackbar.
 * @property {Function} setSnackbarMessage - Manually sets the snackbar message.
 * @property {Function} setSnackbarSeverity - Manually sets the snackbar severity.
 */
export const useAppSnackbar = (): {
  handleError: (error: unknown, defaultErrorMessage?: string) => Promise<void>
  handleMessage: (message: string, severity?: SnackbarSeverityType) => void
  setSnackbarState: (state: boolean) => void
  setSnackbarMessage: (message: string) => void
  setSnackbarSeverity: (severity: SnackbarSeverity) => void
} => {
  const { t } = useTranslation()
  const { setSnackbarMessage, setSnackbarSeverity, setSnackbarState } =
    useSnackbarContext()

  const handleError = async (
    error: unknown,
    defaultErrorMessage = t(
      'Hooks.UseAppSnackbar.Error.Default',
      'An unexpected error occurred'
    )
  ): Promise<void> => {
    const errorObj: ErrorObject = (await extractedErrorObject(error)) ?? {
      code: 'Unknown',
      message: (error as Error)?.message ?? defaultErrorMessage,
    }
    setSnackbarState?.(true)
    setSnackbarMessage?.(errorObj.message)
    setSnackbarSeverity?.(SnackbarSeverity.Error)
  }

  const handleMessage = (
    message: string,
    severity: SnackbarSeverityType = 'success'
  ): void => {
    setSnackbarMessage?.(message)
    setSnackbarSeverity?.(
      SnackbarSeverity[
        (severity.charAt(0).toUpperCase() +
          severity.slice(1)) as keyof typeof SnackbarSeverity
      ]
    )
    setSnackbarState?.(true)
  }

  return {
    handleError,
    handleMessage,
    setSnackbarState,
    setSnackbarMessage,
    setSnackbarSeverity,
  }
}

/**
 * ----------------------------------------
 * 📘 How to use this hook (Example usage)
 * ----------------------------------------
 *
 * import { useAppSnackbar } from '@/hooks/useAppSnackbar'
 * import { useTranslation } from 'react-i18next'
 *
 * const MyComponent = () => {
 *   const { handleError, handleMessage } = useAppSnackbar()
 *   const { t } = useTranslation()
 *
 *   const handleSubmit = async () => {
 *     try {
 *       await apiCall()
 *       handleMessage(
 *         t('Forms.Submit.Success', 'Form submitted successfully!')
 *       )
 *     } catch (error) {
 *       await handleError(error, t('Forms.Submit.Error', 'Could not submit form.'))
 *     }
 *   }
 *
 *   return <Button onClick={handleSubmit}>Submit</Button>
 * }
 *
 * ----------------------------------------
 * 📝 Custom usage with different severity:
 * ----------------------------------------
 *
 * handleMessage('Saved as draft.', 'info')
 * handleMessage('Please check the form inputs.', 'warning')
 */
